import React from "react";
import { Context } from "@scenes/optimizedGantt/context";
import { Views } from "@scenes/optimizedGantt/api/views";
import { Props } from "./types";
import { useElementDrag } from "../utils/drag";
import { useElementEdition } from "../utils/edit";
import { useElementContext } from "../context";
import { DetailsPopup } from "../detailsPopup";
import { getSnapQuantum } from "../root/utils";
import { isDone } from "../utils";

function Draggable({
  dayDiff,
  graphRef,
  project,
  element,
  dates,
  isDisabled = false,
  children,
}: Props) {
  const { editElement } = useElementEdition(element);
  const { state, reducer } = useElementContext();
  const { cell, currentView } = React.useContext(Context);
  const isDaily = currentView === Views.DAILY;
  const quantum = getSnapQuantum(cell, isDaily);
  const canDrag = !isDone(element) && !isDisabled;
  // Anchor in user TZ — `dates[0]` is a DateTime at user-TZ midnight.
  // Drag emissions are user-TZ day midnights to UTC ISO: what the user
  // drops on column N re-renders on column N. The daily view snaps to the
  // hour grid instead and keeps the element's exact duration — rebuilding
  // both dates as midnights would wipe the time-of-day the view displays.
  const anchor = dates[0];
  // dayDiff cascades NaN when the parent Resizeable sees a malformed
  // element.startDate/endDate (Invalid DateTime → .toMillis() === NaN).
  // anchor.plus({ days: NaN }) throws, so fall back to 0 (anchor unchanged).
  const safeDayDiff = Number.isFinite(dayDiff) ? dayDiff : 0;
  const getDropStart = (dragDelta: number) => {
    const snapIndex = Number.isFinite(dragDelta)
      ? Math.round(dragDelta / quantum)
      : 0;

    return isDaily
      ? anchor.plus({ hours: snapIndex })
      : anchor.plus({ days: snapIndex });
  };
  const { startDrag } = useElementDrag(graphRef, project || null, {
    edit: (offset) => reducer.drag((state) => ({ ...state, offset })),
    stopDrag: () => {
      const dropStart = getDropStart(
        state.drag.offset - state.drag.clickInitialPos,
      );

      editElement({
        ...element,
        startDate: dropStart.toUTC().toISO() ?? "",
        endDate:
          (isDaily
            ? dropStart.plus({ days: safeDayDiff })
            : dropStart.plus({ days: Math.round(safeDayDiff) })
          )
            .toUTC()
            .toISO() ?? "",
      });
      reducer.drag((state) => ({
        ...state,
        isDragging: false,
        isPressing: false,
      }));
    },
  });
  const cancelDrag = () =>
    reducer.drag((state) => ({
      ...state,
      isDragging: false,
      isPressing: false,
    }));
  const previewStart = getDropStart(
    state.drag.offset - state.drag.clickInitialPos,
  );

  return (
    <DetailsPopup
      startDate={previewStart.toJSDate()}
      endDate={previewStart.plus({ days: safeDayDiff }).toJSDate()}
      canShowPopup={state.drag.isDragging}
      element={element}
      isLeft
      isRight
    >
      {children(
        {
          onMouseDown: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();

            canDrag &&
              reducer.drag((state) => ({
                ...state,
                isPressing: true,
                clickInitialPos: e.clientX - rect.left,
              }));
          },
          onTouchStart: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();

            canDrag &&
              e.changedTouches.length > 0 &&
              reducer.drag((state) => ({
                ...state,
                isPressing: true,
                clickInitialPos: e.changedTouches[0].clientX - rect.left,
              }));
          },
          onMouseMove: (e) => {
            if (state.drag.isPressing) {
              reducer.drag((state) => ({ ...state, isDragging: true }));
              startDrag(e.clientX);
            }
          },
          onTouchMove: (e) => {
            if (state.drag.isPressing) {
              reducer.drag((state) => ({ ...state, isDragging: true }));
              e.changedTouches.length > 0 &&
                startDrag(e.changedTouches[0].clientX);
            }
          },
        },
        cancelDrag,
      )}
    </DetailsPopup>
  );
}

export type { Props };

export { Draggable };
